import { useQuery } from '@tanstack/react-query'
import { formatPercent } from '@/utils/format'
import { Award, CheckCircle2, FileText, Percent, Trophy, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Card } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatCard } from '@/components/ui/StatCard'
import { cn } from '@/utils/cn'
import { tabulationQuery } from '../api'
import { formatMarks, formatOutOf, formatScore } from '../status'
import type { Exam } from '../types'
import { GradeBadge } from './ExamBadges'

/** Tabulation sheet: students × papers with totals, grade and rank; live preview until published. */
export function ResultsPanel({
  exam,
  reportCardHref,
}: {
  exam: Exam
  reportCardHref: (studentId: number) => string
}) {
  const { t } = useTranslation('exams')
  const [classId, setClassId] = useState<number>(exam.classes[0]?.id ?? 0)
  const [sectionId, setSectionId] = useState<number | undefined>(undefined)
  const cls = exam.classes.find((c) => c.id === classId)
  const tab = useQuery({ ...tabulationQuery(exam.id, classId, sectionId), enabled: classId > 0 })
  const rankKey = sectionId ? 'sectionRank' : 'classRank'
  const outOf = tab.data?.outOf ?? null

  return (
    <div className="space-y-4">
      <Card className="grid gap-4 p-4 sm:grid-cols-3">
        <FormField label={t('Class')}>
          <Select
            value={classId}
            onChange={(e) => {
              setClassId(Number(e.target.value))
              setSectionId(undefined)
            }}
            options={exam.classes.map((c) => ({ value: c.id, label: c.name }))}
          />
        </FormField>
        <FormField label={t('Section')}>
          <Select
            value={sectionId ?? ''}
            placeholder={t('All sections')}
            onChange={(e) => setSectionId(e.target.value === '' ? undefined : Number(e.target.value))}
            options={(cls?.sections ?? []).map((s) => ({ value: s.id, label: s.name || t('Whole class') }))}
          />
        </FormField>
      </Card>

      {tab.isPending ? (
        <Card className="space-y-3 p-5">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-48 w-full" />
        </Card>
      ) : tab.error ? (
        <Card>
          <ErrorState onRetry={() => void tab.refetch()} />
        </Card>
      ) : (
        <>
          {tab.data.isPreview ? (
            <Alert tone="info" title={t('Preview')}>
              {t(
                'These results are calculated live from the marks entered so far. They are frozen when the exam is published.',
              )}
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label={t('Students')} value={tab.data.stats.students} icon={<UsersRound />} />
            <StatCard
              label={t('Pass rate')}
              value={formatPercent(tab.data.stats.passRate)}
              icon={<CheckCircle2 />}
              tone="success"
              hint={t('{{passed}} passed · {{failed}} failed', {
                passed: tab.data.stats.passed,
                failed: tab.data.stats.failed,
              })}
            />
            <StatCard
              label={t('Average')}
              value={
                outOf !== null
                  ? formatOutOf(tab.data.stats.average, outOf)
                  : formatPercent(tab.data.stats.averagePercentage)
              }
              icon={<Percent />}
              tone="info"
            />
            <StatCard
              label={t('Highest')}
              value={
                outOf !== null
                  ? formatOutOf(tab.data.stats.highestAverage, outOf)
                  : formatPercent(tab.data.stats.highestPercentage)
              }
              icon={<Trophy />}
              tone="warning"
            />
          </div>
          <Card className="overflow-hidden">
            {tab.data.rows.length === 0 ? (
              <EmptyState
                icon={<Award />}
                title={t('No results')}
                description={t('No students with papers in this selection.')}
              />
            ) : (
              <div className="max-h-[70vh] scrollbar-thin overflow-auto">
                <table className="w-full border-separate border-spacing-0 text-sm">
                  <caption className="sr-only">
                    {t('Tabulation sheet for {{name}}', { name: cls?.name ?? '' })}
                  </caption>
                  <thead>
                    <tr className="text-2xs font-semibold tracking-wider whitespace-nowrap text-muted uppercase">
                      <th
                        scope="col"
                        className="sticky top-0 left-0 z-20 min-w-48 border-r border-b border-line bg-surface-muted px-4 py-2.5 text-left"
                      >
                        {t('Student')}
                      </th>
                      {tab.data.papers.map((p) => (
                        <th
                          key={p.paperId}
                          scope="col"
                          className="sticky top-0 z-10 border-b border-line bg-surface-muted px-3 py-2.5 text-center"
                          title={p.subjectName}
                        >
                          {p.subjectCode ?? p.subjectName}
                          <span className="block font-normal normal-case">
                            {outOf !== null
                              ? `/ ${formatScore(outOf)} · ${t('Coef. {{value}}', { value: formatScore(p.coefficient) })}`
                              : `/ ${formatMarks(p.maxMarks)}`}
                          </span>
                        </th>
                      ))}
                      <th
                        scope="col"
                        className="sticky top-0 z-10 border-b border-line bg-surface-muted px-3 py-2.5 text-right"
                      >
                        {outOf !== null ? t('Weighted total') : t('Total')}
                      </th>
                      <th
                        scope="col"
                        className="sticky top-0 z-10 border-b border-line bg-surface-muted px-3 py-2.5 text-right"
                      >
                        {outOf !== null ? (
                          <>
                            {t('Average')}
                            <span className="block font-normal normal-case">/ {formatScore(outOf)}</span>
                          </>
                        ) : (
                          '%'
                        )}
                      </th>
                      <th
                        scope="col"
                        className="sticky top-0 z-10 border-b border-line bg-surface-muted px-3 py-2.5 text-center"
                      >
                        {t('Grade')}
                      </th>
                      {outOf === null ? (
                        <th
                          scope="col"
                          className="sticky top-0 z-10 border-b border-line bg-surface-muted px-3 py-2.5 text-right"
                        >
                          {t('GP')}
                        </th>
                      ) : null}
                      <th
                        scope="col"
                        className="sticky top-0 z-10 border-b border-line bg-surface-muted px-3 py-2.5 text-center"
                      >
                        {t('Rank')}
                      </th>
                      <th
                        scope="col"
                        className="sticky top-0 z-10 border-b border-line bg-surface-muted px-3 py-2.5 pr-5"
                      >
                        <span className="sr-only">{t('Report card')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tab.data.rows.map((r) => {
                      const byPaper = new Map(r.papers.map((p) => [p.paperId, p]))
                      return (
                        <tr key={r.studentId} className="group">
                          <th
                            scope="row"
                            className="sticky left-0 z-10 border-r border-b border-line bg-surface px-4 py-2.5 text-left font-normal group-hover:bg-surface-muted"
                          >
                            <p className="font-medium text-ink">{r.fullName}</p>
                            <p className="text-xs text-muted">
                              {r.sectionName ? `${r.sectionName} · ` : ''}
                              {t('Roll {{roll}}', { roll: r.rollNumber ?? '—' })}
                            </p>
                          </th>
                          {tab.data.papers.map((col) => {
                            const p = byPaper.get(col.paperId)
                            return (
                              <td
                                key={col.paperId}
                                className={cn(
                                  'border-b border-line px-3 py-2.5 text-center tabular group-hover:bg-surface-muted',
                                  p && !p.isPass && 'text-danger-700',
                                )}
                              >
                                {!p ? (
                                  '—'
                                ) : p.isAbsent ? (
                                  <span className="text-xs font-medium">{t('AB')}</span>
                                ) : p.isMissing ? (
                                  <span className="text-subtle">·</span>
                                ) : outOf !== null ? (
                                  formatScore(p.note)
                                ) : (
                                  formatMarks(p.marksObtained)
                                )}
                                {outOf !== null ? (
                                  p && !p.isMissing ? (
                                    <span
                                      className="block text-2xs text-muted"
                                      title={t('Mark × coefficient')}
                                    >
                                      × {formatScore(p.coefficient)} = {formatScore(p.weighted)}
                                    </span>
                                  ) : null
                                ) : p?.grade && !p.isAbsent ? (
                                  <span className="block text-2xs text-muted">{p.grade}</span>
                                ) : null}
                              </td>
                            )
                          })}
                          <td className="border-b border-line px-3 py-2.5 text-right font-medium text-ink tabular group-hover:bg-surface-muted">
                            {outOf !== null ? (
                              <>
                                {formatScore(r.weightedTotal)}
                                <span className="block text-2xs font-normal text-muted">
                                  / {formatScore(r.maxWeightedTotal)}
                                </span>
                              </>
                            ) : (
                              formatMarks(r.totalObtained)
                            )}
                          </td>
                          <td className="border-b border-line px-3 py-2.5 text-right tabular group-hover:bg-surface-muted">
                            {outOf !== null ? (
                              <span className="font-semibold text-ink">{formatScore(r.average, 2)}</span>
                            ) : (
                              formatMarks(r.percentage)
                            )}
                          </td>
                          <td className="border-b border-line px-3 py-2.5 text-center group-hover:bg-surface-muted">
                            <GradeBadge grade={r.grade} isPass={r.isPass} />
                          </td>
                          {outOf === null ? (
                            <td className="border-b border-line px-3 py-2.5 text-right tabular group-hover:bg-surface-muted">
                              {r.gradePoint === null ? '—' : r.gradePoint.toFixed(2)}
                            </td>
                          ) : null}
                          <td className="border-b border-line px-3 py-2.5 text-center font-semibold text-ink tabular group-hover:bg-surface-muted">
                            {r[rankKey]}
                          </td>
                          <td className="border-b border-line px-3 py-2.5 pr-5 text-right group-hover:bg-surface-muted">
                            <Link
                              to={reportCardHref(r.studentId)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
                              aria-label={t('Report card of {{name}}', { name: r.fullName })}
                            >
                              <FileText className="size-3.5" aria-hidden="true" /> {t('Card')}
                            </Link>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <p className="text-xs text-muted">
            {outOf !== null
              ? t(
                  'Marks are out of {{outOf}}; each is multiplied by the subject coefficient. Average = weighted total ÷ total coefficients. AB = absent (counts as 0). Red marks are below the pass mark. Equal averages share a rank.',
                  { outOf },
                )
              : t(
                  'AB = absent (counts as a failed paper). Red marks are below the pass mark. Equal percentage and total share a rank.',
                )}
          </p>
        </>
      )}
    </div>
  )
}
